<script>
const MINUTE_IN_MILLI_SECONDS = 60000;
const HOUR_IN_MILLI_SECONDS = MINUTE_IN_MILLI_SECONDS * 60;
const DAY_IN_MILLI_SECONDS = HOUR_IN_MILLI_SECONDS * 24;

import {
  dynamicTime,
  dateFormat,
  shortTimestamp,
} from 'shared/helpers/timeHelper';
import { formatDistanceToNowStrict, fromUnixTime, format } from 'date-fns';
import { zhCN, zhTW } from 'date-fns/locale';

const ZH_DATE_FORMAT = 'yyyy年M月d日';

export default {
  name: 'TimeAgo',
  props: {
    isAutoRefreshEnabled: {
      type: Boolean,
      default: true,
    },
    lastActivityTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    createdAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
  },
  data() {
    return {
      lastActivityAtTimeAgo: dynamicTime(this.lastActivityTimestamp),
      createdAtTimeAgo: dynamicTime(this.createdAtTimestamp),
      timer: null,
    };
  },
  computed: {
    isChineseLocale() {
      const rawLocale = this.$i18n?.locale;
      const localeValue =
        typeof rawLocale === 'string' ? rawLocale : rawLocale?.value;
      return localeValue?.toLowerCase().startsWith('zh');
    },
    lastActivityTime() {
      return this.formatShortTime(
        this.lastActivityTimestamp,
        this.lastActivityAtTimeAgo
      );
    },
    createdAtTime() {
      return this.formatShortTime(
        this.createdAtTimestamp,
        this.createdAtTimeAgo
      );
    },
    createdAt() {
      const createdTimeDiff = Date.now() - this.createdAtTimestamp * 1000;
      const isBeforeAMonth = createdTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isBeforeAMonth
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.LATEST')} ${this.formatRelativeTimeWithSuffix(
            this.createdAtTimestamp,
            this.createdAtTimeAgo
          )}`
        : `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.OLDEST')} ${this.formatDateForLocale(
            this.createdAtTimestamp
          )}`;
    },
    lastActivity() {
      const lastActivityTimeDiff =
        Date.now() - this.lastActivityTimestamp * 1000;
      const isNotActive = lastActivityTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isNotActive
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.ACTIVE')} ${this.formatRelativeTimeWithSuffix(
            this.lastActivityTimestamp,
            this.lastActivityAtTimeAgo
          )}`
        : `${this.$t(
            'CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.NOT_ACTIVE'
          )} ${this.formatDateForLocale(this.lastActivityTimestamp)}`;
    },
    tooltipText() {
      return `${this.createdAt}
              ${this.lastActivity}`;
    },
    displayText() {
      if (this.isChineseLocale) {
        return `创建 ${this.createdAtTime}前 · 活跃 ${this.lastActivityTime}前`;
      }
      return `${this.createdAtTime} • ${this.lastActivityTime}`;
    },
  },
  watch: {
    lastActivityTimestamp() {
      this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
    },
    createdAtTimestamp() {
      this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
    },
  },
  mounted() {
    if (this.isAutoRefreshEnabled) {
      this.createTimer();
    }
  },
  unmounted() {
    clearTimeout(this.timer);
  },
  methods: {
    getChineseDateFnsLocale() {
      const rawLocale = this.$i18n?.locale;
      const localeValue =
        typeof rawLocale === 'string' ? rawLocale : rawLocale?.value;
      const normalized = localeValue?.toLowerCase().replace(/_/g, '-');
      if (!normalized) return zhCN;
      if (
        normalized.includes('tw') ||
        normalized.includes('hk') ||
        normalized.includes('mo') ||
        normalized.includes('hant')
      ) {
        return zhTW;
      }
      return zhCN;
    },
    formatShortTime(timestamp, englishRelativeTime) {
      if (!timestamp) return '';
      if (!this.isChineseLocale) {
        return shortTimestamp(englishRelativeTime);
      }
      const locale = this.getChineseDateFnsLocale();
      const date = fromUnixTime(timestamp);
      return formatDistanceToNowStrict(date, { locale }).replace(/\s+/g, '');
    },
    formatRelativeTimeWithSuffix(timestamp, englishRelativeTime) {
      if (!timestamp) return '';
      if (!this.isChineseLocale) return englishRelativeTime;
      const locale = this.getChineseDateFnsLocale();
      const date = fromUnixTime(timestamp);
      return formatDistanceToNowStrict(date, {
        addSuffix: true,
        locale,
      }).replace(/\s+/g, '');
    },
    formatDateForLocale(timestamp) {
      if (!timestamp) return '';
      if (!this.isChineseLocale) return dateFormat(timestamp);
      const locale = this.getChineseDateFnsLocale();
      return format(fromUnixTime(timestamp), ZH_DATE_FORMAT, { locale });
    },
    createTimer() {
      this.timer = setTimeout(() => {
        this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
        this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
        this.createTimer();
      }, this.refreshTime());
    },
    refreshTime() {
      const timeDiff = Date.now() - this.lastActivityTimestamp * 1000;
      if (timeDiff > DAY_IN_MILLI_SECONDS) {
        return DAY_IN_MILLI_SECONDS;
      }
      if (timeDiff > HOUR_IN_MILLI_SECONDS) {
        return HOUR_IN_MILLI_SECONDS;
      }

      return MINUTE_IN_MILLI_SECONDS;
    },
  },
};
</script>

<template>
  <div
    v-tooltip.top="{
      content: tooltipText,
      delay: { show: 1000, hide: 0 },
      hideOnClick: true,
    }"
    class="ml-auto leading-4 text-xxs text-n-slate-10 hover:text-n-slate-11"
  >
    <span>{{ displayText }}</span>
  </div>
</template>
